import { supabaseAdapter } from '@/adapters/supabase';
import type { ComponentType } from 'react';
import { PrivacyPolicyContent } from '@/components/legal/content/PrivacyPolicyContent';
import { TermsOfServiceContent } from '@/components/legal/content/TermsOfServiceContent';
import { CookiePolicyContent } from '@/components/legal/content/CookiePolicyContent';
import { ShippingPolicyContent } from '@/components/legal/content/ShippingPolicyContent';
import { RefundPolicyContent } from '@/components/legal/content/RefundPolicyContent';

export interface LegalPageData {
  id: string;
  slug: string;
  title: string;
  description: string;
  hero_banner_src: string | null;
  hero_banner_alt: string | null;
  last_updated: string;
  is_active: boolean;
  display_order: number;
}

export interface LegalPage extends LegalPageData {
  ContentComponent: ComponentType;
}

// Map slugs to their corresponding content components
const contentComponentMap: Record<string, ComponentType> = {
  'privacy-policy': PrivacyPolicyContent,
  'terms-of-service': TermsOfServiceContent,
  'cookie-policy': CookiePolicyContent,
  'shipping-policy': ShippingPolicyContent,
  'refund-policy': RefundPolicyContent,
};


// Fallback data for when database is not available
const fallbackLegalData: Omit<LegalPageData, 'id' | 'is_active' | 'display_order'>[] = [
  {
    slug: 'privacy-policy',
    title: 'Privacy Policy',
    description: 'How we collect, use, and protect your personal information.',
    hero_banner_src: '/images/legal/privacy-banner.jpg',
    hero_banner_alt: 'Privacy Policy Banner',
    last_updated: '2025-05-26',
  },
  {
    slug: 'terms-of-service',
    title: 'Terms of Service',
    description: 'The rules and guidelines for using our website and services.',
    hero_banner_src: '/images/legal/terms-banner.jpg',
    hero_banner_alt: 'Terms of Service Banner',
    last_updated: '2025-05-26',
  },
  {
    slug: 'cookie-policy',
    title: 'Cookie Policy',
    description: 'How we use cookies and other tracking technologies.',
    hero_banner_src: '/images/legal/cookies-banner.jpg',
    hero_banner_alt: 'Cookie Policy Banner',
    last_updated: '2025-05-26',
  },
  {
    slug: 'shipping-policy',
    title: 'Shipping Policy',
    description: 'Information about our shipping methods, costs, and delivery times.',
    hero_banner_src: '/images/legal/shipping-banner.jpg',
    hero_banner_alt: 'Shipping Policy Banner',
    last_updated: '2025-05-26',
  },
  {
    slug: 'refund-policy',
    title: 'Refund Policy',
    description: 'Our policy on returns, refunds, and exchanges.',
    hero_banner_src: '/images/legal/refund-banner.jpg',
    hero_banner_alt: 'Refund Policy Banner',
    last_updated: '2025-05-26',
  },
];

export class LegalService {
  private static get supabase() {
    return supabaseAdapter.client;
  }


  /**
   * Get all active legal pages ordered by display_order
   */
  private static useFallback(slug?: string): LegalPage[] | LegalPage | null {
    if (slug) {
      const fallbackPage = fallbackLegalData.find(page => page.slug === slug);
      if (!fallbackPage) return null;
      
      return {
        ...fallbackPage,
        id: `fallback-${slug}`,
        is_active: true,
        display_order: 0,
        ContentComponent: contentComponentMap[fallbackPage.slug] || (() => null),
      };
    }
    
    return fallbackLegalData.map((pageData, index) => ({
      ...pageData,
      id: `fallback-${pageData.slug}`,
      is_active: true,
      display_order: index + 1,
      ContentComponent: contentComponentMap[pageData.slug] || (() => null),
    }));
  }

  static async getAllLegalPages(): Promise<LegalPage[]> {
    try {
      const { data, error } = await this.supabase
        .from('legal_pages')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (error) {
        console.warn('Database not available for getAllLegalPages, using fallback data:', error.message);
        return this.useFallback() as LegalPage[];
      }

      return data.map(pageData => ({
        ...pageData,
        ContentComponent: contentComponentMap[pageData.slug] || (() => null),
      }));
    } catch (error) {
      console.warn('Database connection failed for getAllLegalPages, using fallback data');
      return this.useFallback() as LegalPage[];
    }
  }

  /**
   * Get a specific legal page by slug
   */
  static async getLegalPageBySlug(slug: string): Promise<LegalPage | null> {
    try {
      const { data, error } = await this.supabase
        .from('legal_pages')
        .select('*')
        .eq('slug', slug)
        .eq('is_active', true)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          return this.useFallback(slug) as LegalPage | null;
        }
        console.warn(`Database error for getLegalPageBySlug ('${slug}'), using fallback:`, error.message);
        return this.useFallback(slug) as LegalPage | null;
      }

      return {
        ...data,
        ContentComponent: contentComponentMap[data.slug] || (() => null),
      };
    } catch (error) {
      console.warn(`Database connection failed for getLegalPageBySlug ('${slug}'), using fallback`);
      return this.useFallback(slug) as LegalPage | null;
    }
  }

  /**
   * Get legal page hero data only (for better performance when only hero is needed)
   */
  static async getLegalPageHero(slug: string): Promise<Omit<LegalPageData, 'is_active' | 'display_order'> | null> {
    try {
      const { data, error } = await this.supabase
        .from('legal_pages')
        .select('id, slug, title, description, hero_banner_src, hero_banner_alt, last_updated')
        .eq('slug', slug)
        .eq('is_active', true)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          const fallbackPage = fallbackLegalData.find(page => page.slug === slug);
          return fallbackPage ? { ...fallbackPage, id: `fallback-${slug}` } : null;
        }
        console.warn(`Database error for getLegalPageHero ('${slug}'), using fallback:`, error.message);
        const fallbackPage = fallbackLegalData.find(page => page.slug === slug);
        return fallbackPage ? { ...fallbackPage, id: `fallback-${slug}` } : null;
      }

      return data;
    } catch (error) {
      console.warn(`Database connection failed for getLegalPageHero ('${slug}'), using fallback`);
      const fallbackPage = fallbackLegalData.find(page => page.slug === slug);
      return fallbackPage ? { ...fallbackPage, id: `fallback-${slug}` } : null;
    }
  }

  /**
   * Check if a legal page exists and is active
   */
  static async isValidLegalPage(slug: string): Promise<boolean> {
    try {
      const { data, error } = await this.supabase
        .from('legal_pages')
        .select('id')
        .eq('slug', slug)
        .eq('is_active', true)
        .single();

      if (error) {
        return fallbackLegalData.some(page => page.slug === slug);
      }

      return !!data;
    } catch (error) {
      return fallbackLegalData.some(page => page.slug === slug);
    }
  }
}

// Static contact data (this doesn't need to be dynamic)
export const legalContactData = {
  title: 'Contact Information',
  introText: 'For any questions or concerns regarding our legal policies, please contact us using the information below.',
  companyName: 'PAVISHA',
  address: 'Pavisha Pet Industries, Mahadeo Asthan, March Marchi Rd, near Bypass Thana, Paijawa, Patna, Bihar 800007',
  phone: '79799 12640',
  phoneHref: 'tel:+917979912640',
  email: 'sales@pavisha.com',
  emailHref: 'mailto:sales@pavisha.com',
};
